<template>
  <ApplicantInfoHeader
    :index
    :name="buildFullname(applicant.passport)"
    :collapsable="sections.length > 1"
    :collapsed="!openedPanels.length"
    @collapse="collapseAll"
  />
  <v-expansion-panels v-model="openedPanels" class="mb-7" focusable multiple>
    <v-expansion-panel
      v-for="({ icon, name }, i) in sections"
      :value="i"
      ref="panelRefs"
    >
      <v-expansion-panel-title>
        <AppIcon :icon start /> {{ $tt(`applicant_info.${name}`) }}
      </v-expansion-panel-title>
      <v-expansion-panel-text class="body" eager>
        <slot :name />
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import type { Applicant } from '~/types';
import type { VExpansionPanel } from 'vuetify/components';
import { buildIncrementalArray } from '~/helpers/general';
import { buildFullname } from '~/helpers/format';

const SECTIONS = [
  {
    name: 'passport',
    icon: 'mdi:passport'
  }, {
    name: 'personal',
    icon: 'mdi:badge-account-horizontal-outline'
  }, {
    name: 'family',
    icon: 'mdi:human-male-female-child'
  }, {
    name: 'occupation',
    icon: 'mdi:briefcase-outline'
  }, {
    name: 'pastTravels',
    icon: 'mdi:airplane-takeoff'
  }
] as const;

type Section = typeof SECTIONS[number]['name'];

const props = defineProps<{
  applicant: Applicant;
  index: number;
  hiddenSections?: Section[];
}>();
const slots = defineSlots<{ [k in Section]: any; }>();

const openedPanels = ref(buildIncrementalArray(SECTIONS.length));
const panelRefs = useTemplateRef<VExpansionPanel[]>('panelRefs');

const sections = computed(() => SECTIONS.filter(s => slots[s.name] && !props.hiddenSections?.includes(s.name)));

listenForErrorsOnSubmit();

function expand(i: number) {
  const copy = [...openedPanels.value];
  if (!copy.includes(i)) copy.push(i);
  openedPanels.value = copy;
}

function collapseAll() {
  openedPanels.value = [];
}

function listenForErrorsOnSubmit() {
  useEmitter().formValidationFailedBus.on(field => {
    for (const ref of panelRefs.value!) {
      if (ref.$el.contains(field)) {
        expand(ref.$props.value);
        break;
      }
    }
  });
}
</script>

<style scoped>
.body {
  padding-top: var(--space-6);
}
</style>